import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { BusinessProfileStatus } from '../../models/business/BusinessProfileStatus'
import { businessProfileStatusSchema } from '../../models/schema'

import {
  createBusinessProfileStatus as createBusinessProfileStatusAPI,
  deleteBusinessProfileStatus as deleteBusinessProfileStatusAPI,
  fetchBusinessProfileStatusById,
  fetchBusinessProfileStatuses,
  updateBusinessProfileStatus as updateBusinessProfileStatusAPI,
} from '@services/business/BusinessProfileStatusService'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<BusinessProfileStatus> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'businessProfileStatus',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getBusinessProfileStatusesSuccess,
  setQuery: setBusinessProfileStatusQuery,
  reset: resetBusinessProfileStatusState,
  resetQueryAndIds: resetBusinessProfileStatusQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: BusinessProfileStatus }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectBusinessProfileStatuses = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedBusinessProfileStatus = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectBusinessProfileStatusById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectBusinessProfileStatusesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedBusinessProfileStatus =
  (businessProfileStatus: BusinessProfileStatus | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<BusinessProfileStatus>(businessProfileStatus)
      )
    )

export const getBusinessProfileStatuses = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchBusinessProfileStatuses(params),
    [businessProfileStatusSchema],
    slice.actions
  )

export const createBusinessProfileStatus = (
  businessProfileStatus: BusinessProfileStatus
) =>
  SBAPI.CreateDispatched(
    createBusinessProfileStatusAPI(businessProfileStatus),
    businessProfileStatusSchema,
    slice.actions
  )

export const getBusinessProfileStatusWithId = (id: number) =>
  SBAPI.FetchDispatched<BusinessProfileStatus>(
    fetchBusinessProfileStatusById(id),
    businessProfileStatusSchema,
    slice.actions
  )

export const updateBusinessProfileStatus = (
  businessProfileStatus: BusinessProfileStatus
) =>
  SBAPI.UpdateDispatched<BusinessProfileStatus>(
    updateBusinessProfileStatusAPI(businessProfileStatus),
    businessProfileStatus,
    slice.actions
  )

export const deleteBusinessProfileStatus = (
  businessProfileStatus: BusinessProfileStatus
) =>
  SBAPI.DestroyDispatched<BusinessProfileStatus>(
    deleteBusinessProfileStatusAPI(businessProfileStatus),
    businessProfileStatus,
    slice.actions
  )
